import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Ban, ExternalLink, ReceiptText, Undo2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS, PAYMENT_METHOD_LABELS } from '@/utils/constants';
import { TransactionDetail, ApiResponse, RefundRequest, RefundResponse, VoidRequest } from '@/services/types';
import { Alert, Badge, Button, Card, Chip, Input, LoadingSkeleton, Sheet, SummaryLine, Switch } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { formatDateTime, formatCurrency } from '@/utils/formatters';
import { useAppSelector } from '@/app/hooks';
import { ReceiptViewer } from './ReceiptViewer';
import { parseOrderType } from '@/utils/orderType';

interface TransactionDetailsProps {
    receiptNumber: string;
    shopId: number;
    onClose: () => void;
    /** Landscape: render in the right column instead of as a full-screen page */
    inline?: boolean;
}

/** 11a Detail transaksi (+ 11b Pengembalian / void sheet). Void stays gated to MANAGER | ADMIN. */
export const TransactionDetails: React.FC<TransactionDetailsProps> = ({ receiptNumber, shopId, onClose, inline = false }) => {
    const { t } = useTranslation();
    const [details, setDetails] = useState<TransactionDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showReceipt, setShowReceipt] = useState(false);
    const role = useAppSelector((s) => s.auth.selectedShop?.role);
    const canVoid = role === 'MANAGER' || role === 'ADMIN';

    const [action, setAction] = useState<'void' | 'refund' | null>(null);
    const [reason, setReason] = useState('');
    const [refundAmount, setRefundAmount] = useState<number>(0);
    const [restoreStock, setRestoreStock] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const openAction = (a: 'void' | 'refund') => {
        setReason('');
        setRefundAmount(details?.grandTotal ?? 0);
        setRestoreStock(true);
        setAction(a);
    };

    const submitAction = async () => {
        if (!details || !action) return;
        if (!reason.trim()) { toast.error(t('transactions.reasonRequired')); return; }
        if (action === 'refund' && (refundAmount <= 0 || refundAmount > details.grandTotal)) {
            toast.error(t('transactions.invalidRefundAmount'));
            return;
        }
        setSubmitting(true);
        try {
            if (action === 'void') {
                const body: VoidRequest = { reason: reason.trim() };
                await api.post(API_ENDPOINTS.VOID_TRANSACTION(shopId, details.receiptNumber), body);
                toast.success(t('transactions.voidSuccess'));
            } else {
                const body: RefundRequest = {
                    idempotencyKey: uuidv4(),
                    originalReceiptNumber: details.receiptNumber,
                    refundAmount,
                    reason: reason.trim(),
                    restoreStock,
                };
                const res = await api.post<ApiResponse<RefundResponse>>(API_ENDPOINTS.REFUND(shopId), body);
                toast.success(t('transactions.refundSuccess', { receipt: res.data.data.refundReceiptNumber }));
            }
            setAction(null);
            fetchTransactionDetails();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => { fetchTransactionDetails(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receiptNumber]);

    const fetchTransactionDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<ApiResponse<TransactionDetail>>(API_ENDPOINTS.TRANSACTION_DETAILS(shopId, receiptNumber));
            setDetails(response.data.data);
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const status = details && (
        details.isVoided ? <Badge tone="danger" dot>{t('transactions.cancelled')}</Badge>
        : details.invoice?.status === 'WAITINGFORPAYMENT' ? <Badge tone="warning" dot>{t('transactions.pending')}</Badge>
        : <Badge tone="success" dot>{t('transactions.paid')}</Badge>
    );
    const methodLabel = details?.invoice ? (PAYMENT_METHOD_LABELS[details.invoice.bankCode.toUpperCase()] ?? details.invoice.bankCode) : '-';
    const orderType = parseOrderType(details?.notes).orderType;
    const orderTypeLabel = orderType ? t(`cart.orderTypes.${orderType}` as 'cart.orderTypes.DINE_IN') : details?.type ?? '—';

    const body = (
        <div className="flex flex-col gap-3 pb-6 lg:gap-4">
            {loading && !details && <><LoadingSkeleton height={120} /><LoadingSkeleton height={160} /></>}
            {error && <Alert variant="error" message={error} />}
            {details && (
                <>
                    <Card padding="md" className="flex flex-col gap-1.5 !rounded-xl lg:p-[22px_24px]">
                        <div className="flex items-center justify-between gap-2">
                            {status}
                            <span className="text-xs text-ink-muted">{formatDateTime(details.createdAt)}</span>
                        </div>
                        <span className="text-[36px] font-black leading-[1.1] tnum tracking-[-0.02em] text-ink-brand lg:text-[42px]">{formatCurrency(details.grandTotal)}</span>
                        <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line-subtle pt-3.5 text-[13px] lg:grid-cols-4">
                            {[[t('transactions.cashier'), details.cashierName], [t('transactions.method'), methodLabel], [t('transactions.orderType'), orderTypeLabel], [t('heldCarts.customer'), [details.customerName, details.customerPhone].filter(Boolean).join(' · ') || '—']].map(([l, v]) => (
                                <div key={l} className="flex min-w-0 flex-col"><span className="text-ink-muted">{l}</span><span className="truncate font-extrabold">{v}</span></div>
                            ))}
                        </div>
                    </Card>

                    <Card padding="sm" className="flex flex-col gap-2.5 text-sm tnum lg:p-[16px_20px]">
                        {details.items.map((item, i) => (
                            <div key={i} className="flex justify-between gap-3">
                                <span className="min-w-0 flex-1 truncate">{item.quantity}× {item.name}{item.discount > 0 && <span className="ml-1 text-xs text-success-600">−{formatCurrency(item.discount)}</span>}</span>
                                <span className="font-bold">{formatCurrency(item.lineTotal)}</span>
                            </div>
                        ))}
                        <div className="border-t border-line-subtle" />
                        {details.discountTotal > 0 && <SummaryLine kind="discount" label={t('cart.discount')} value={`−${formatCurrency(details.discountTotal)}`} />}
                        <SummaryLine label={t('cart.tax')} value={formatCurrency(details.taxTotal)} />
                        {details.serviceChargeTotal > 0 && <SummaryLine label={t('cart.serviceCharge')} value={formatCurrency(details.serviceChargeTotal)} />}
                        {details.invoice && details.invoice.paymentFee > 0 && <SummaryLine label={t('transactions.paymentFee')} value={formatCurrency(details.invoice.paymentFee)} />}
                        <SummaryLine label={t('transactions.paidChange')} value={`${formatCurrency(details.paymentAmount)} · ${formatCurrency(details.changeAmount)}`} />
                    </Card>

                    {details.invoice && (
                        <p className="px-1 text-xs text-ink-muted tnum">{t('checkout.invoiceId')} {details.invoice.invoiceIdentifier} · {t('checkout.billId')} {details.invoice.billId}</p>
                    )}

                    <div className="hidden flex-1 lg:block" />
                    <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
                        <div className="grid grid-cols-2 gap-2.5 lg:flex">
                            <button type="button" onClick={() => setShowReceipt(true)} className="flex h-12 items-center justify-center gap-2 rounded-md bg-surface-brand px-[18px] text-sm font-extrabold text-white shadow-brand lg:h-12">
                                <ReceiptText className="h-[18px] w-[18px]" />{t('transactions.receipt')}
                            </button>
                            {details.invoice?.status === 'WAITINGFORPAYMENT' && details.invoice.paymentUrl && (
                                <button type="button" onClick={() => window.open(details.invoice!.paymentUrl, '_blank')} className="flex h-12 items-center justify-center gap-2 rounded-md border border-line bg-surface-card px-[18px] text-sm font-extrabold text-ink-brand">
                                    <ExternalLink className="h-[18px] w-[18px]" />{t('checkout.payment')}
                                </button>
                            )}
                        </div>
                        <div className="hidden flex-1 lg:block" />
                        {!details.isVoided && (
                            <div className="grid grid-cols-2 gap-2.5 lg:flex">
                                <Button variant="outline" size="md" icon={<Undo2 />} disabled onClick={() => openAction('refund')}>{t('transactions.refund')}</Button>
                                {canVoid && <Button variant="ghost" size="md" icon={<Ban />} disabled onClick={() => openAction('void')} className="!text-status-danger">{t('transactions.void')}</Button>}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const sheets = (
        <>
            <Sheet
                isOpen={action !== null}
                onClose={() => setAction(null)}
                title={action === 'void' ? t('transactions.void') : t('transactions.processRefund')}
                size="lg"
                footer={
                    <div className="flex gap-2.5">
                        <Button variant="ghost" size="lg" fullWidth onClick={() => setAction(null)} disabled={submitting}>{t('cart.cancel')}</Button>
                        <Button variant={action === 'void' ? 'danger' : 'primary'} size="lg" fullWidth icon={action === 'void' ? <Ban /> : <Undo2 />} onClick={submitAction} loading={submitting}>
                            {action === 'void' ? t('cart.confirm') : t('transactions.processWithAmount', { amount: formatCurrency(refundAmount) })}
                        </Button>
                    </div>
                }
            >
                {details && (
                    <div className="flex flex-col gap-3">
                        <Card padding="sm" className="flex flex-col gap-1 !rounded-xl">
                            <div className="flex items-center justify-between"><span className="text-sm font-extrabold">{details.receiptNumber}</span>{status}</div>
                            <span className="text-xs text-ink-muted">{formatDateTime(details.createdAt)} · {methodLabel} · {details.cashierName}</span>
                            <span className="text-2xl font-black tnum text-ink-brand">{formatCurrency(details.grandTotal)}</span>
                        </Card>
                        {action === 'refund' && (
                            <>
                                <Card padding="sm" className="flex flex-col gap-1.5 text-sm tnum">
                                    {details.items.map((it, i) => <div key={i} className="flex justify-between gap-3"><span className="truncate"><strong>{it.quantity}×</strong> {it.name}</span><span className="font-bold">{formatCurrency(it.lineTotal)}</span></div>)}
                                </Card>
                                <span className="text-[13px] font-extrabold text-ink-secondary">{t('transactions.refundAmount')}</span>
                                <Input
                                    fieldSize="lg" numeric type="number" min={0} max={details.grandTotal} inputMode="numeric"
                                    value={refundAmount || ''} onChange={(e) => setRefundAmount(parseFloat(e.target.value) || 0)}
                                    leftIcon={<span className="text-xl font-extrabold text-ink-muted">Rp</span>}
                                    wrapperClassName="!h-16 !border-2 !border-accent-500 shadow-focus" className="!text-[28px]"
                                    helperText={t('transactions.maxRefund', { amount: formatCurrency(details.grandTotal) })}
                                />
                                <div className="flex flex-wrap gap-2">
                                    <Chip selected={refundAmount === details.grandTotal} onClick={() => setRefundAmount(details.grandTotal)}>{t('transactions.allAmount', { amount: formatCurrency(details.grandTotal) })}</Chip>
                                    {details.items.map((it, i) => <Chip key={i} selected={refundAmount === it.lineTotal} onClick={() => setRefundAmount(it.lineTotal)}>{formatCurrency(it.lineTotal)}</Chip>)}
                                </div>
                            </>
                        )}
                        <Input label={t('transactions.reason')} multiline rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('transactions.reasonPlaceholder')} required />
                        {action === 'refund' && (
                            <div className="flex items-center gap-3.5 rounded-lg border border-line-subtle bg-surface-card px-4 py-3.5">
                                <span className="flex min-w-0 flex-1 flex-col"><span className="text-sm font-extrabold">{t('transactions.restoreStock')}</span><span className="text-xs text-ink-muted">{t('transactions.restoreStockHint')}</span></span>
                                <Switch checked={restoreStock} onChange={setRestoreStock} />
                            </div>
                        )}
                        {action === 'void' && <Alert variant="warning" message={t('transactions.voidWarning')} />}
                    </div>
                )}
            </Sheet>
            <ReceiptViewer receiptNumber={receiptNumber} shopId={shopId} isOpen={showReceipt} onClose={() => setShowReceipt(false)} />
        </>
    );

    if (inline) {
        return (
            <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-[22px_28px]">
                <div className="flex items-center gap-3"><h1 className="flex-1 truncate text-[22px] font-black">{receiptNumber}</h1></div>
                {body}
                {sheets}
            </div>
        );
    }

    return createPortal(
        <div className={clsx('fixed inset-0 z-[700] flex flex-col bg-surface-page')}>
            <PageShell title={receiptNumber} onBack={onClose}>{body}</PageShell>
            {sheets}
        </div>,
        document.body,
    );
};

export default TransactionDetails;
